import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LOVABLE_MODELS, BYOK_MODELS_CATALOG, type ModelMeta } from "@/lib/aiModels";

/**
 * Returns the unified catalog of models available to the org:
 * - Lovable Gateway models (always available)
 * - BYOK models that are registered AND active in syra_ai_providers
 */
export function useAvailableModels() {
  return useQuery({
    queryKey: ["available-models"],
    queryFn: async (): Promise<ModelMeta[]> => {
      const { data: providers } = await supabase
        .from("syra_ai_providers_safe")
        .select("provider_name, models, active")
        .eq("active", true);

      const activeByokIds = new Set<string>();
      for (const p of providers ?? []) {
        const models: string[] = (p.models as string[]) || [];
        for (const m of models) activeByokIds.add(m);
      }

      const byok = BYOK_MODELS_CATALOG.filter(
        (m) => activeByokIds.has(m.id) || activeByokIds.has(m.id.split("/").pop() ?? ""),
      );

      // Also surface unknown BYOK models registered by the user
      const knownIds = new Set([...LOVABLE_MODELS, ...BYOK_MODELS_CATALOG].map((m) => m.id));
      const unknownByok: ModelMeta[] = [];
      for (const id of activeByokIds) {
        const fullId = id.includes("/") ? id : `unknown/${id}`;
        if (!knownIds.has(fullId) && !knownIds.has(id)) {
          unknownByok.push({
            id: fullId,
            label: id,
            tier: "balanced",
            provider: fullId.split("/")[0],
            source: "byok",
            theoretical_score: 75,
          });
        }
      }

      return [...LOVABLE_MODELS, ...byok, ...unknownByok];
    },
    staleTime: 60_000,
  });
}

export function groupModelsByTier(models: ModelMeta[]) {
  return {
    fast: models.filter((m) => m.tier === "fast"),
    balanced: models.filter((m) => m.tier === "balanced"),
    reasoning: models.filter((m) => m.tier === "reasoning"),
  };
}
